import { useLingui } from "@lingui/react/macro";
import type { MessageRating, RunUsage, ThreadMessage } from "@rakazo/contracts";
import { MESSAGE_RATINGS } from "@rakazo/contracts";
import { formatRunUsage, replyFooterIds } from "@rakazo/core";
import { Button } from "@rakazo/ui-web";

/** What the transcript needs to draw ratings and usage under a bot chat's replies. */
export type ReplyDetails = {
  ratings: ReadonlyMap<string, MessageRating>;
  /** Each finished run's usage, shown under its reply. */
  usage: ReadonlyMap<string, RunUsage>;
  onRate: (message: ThreadMessage, rating: MessageRating | null) => void;
};

export { formatRunUsage, replyFooterIds };

export function ReplyFooter({
  message,
  rating,
  usage,
  onRate,
}: {
  message: ThreadMessage;
  rating: MessageRating | undefined;
  usage: RunUsage | undefined;
  onRate: (message: ThreadMessage, rating: MessageRating | null) => void;
}) {
  const { t, i18n } = useLingui();
  const labels: Record<MessageRating, string> = {
    perfect: t`Perfect`,
    good: t`Good`,
    average: t`Average`,
    bad: t`Bad`,
  };
  return (
    <div
      data-testid="reply-footer"
      className="mt-1 flex w-full min-w-0 flex-col items-start gap-0.5 ps-1"
    >
      {/* biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a form fieldset. */}
      <div role="group" aria-label={t`Rate this reply`} className="flex flex-wrap gap-0.5">
        {MESSAGE_RATINGS.map((option) => {
          const selected = rating === option;
          return (
            <Button
              key={option}
              size="xs"
              variant={selected ? "outline" : "text"}
              aria-pressed={selected}
              data-rating={option}
              onClick={() => onRate(message, selected ? null : option)}
              className="rounded-full px-2 font-normal"
            >
              {labels[option]}
            </Button>
          );
        })}
      </div>
      {usage ? (
        // One line under the reply, cut short rather than wrapped into a column.
        <p
          data-testid="reply-usage"
          className="w-full min-w-0 truncate whitespace-nowrap text-start text-xs text-faint-foreground tabular-nums"
        >
          {formatRunUsage(usage, i18n.locale || "en")}
        </p>
      ) : null}
    </div>
  );
}
